import {
  Box,
  Avatar,
  Typography,
  Card,
  styled,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Collapse,
  Tooltip
} from '@mui/material';

import {
  formatDistance,
  format,
  subDays,
  subHours,
  subMinutes
} from 'date-fns';
import ScheduleTwoToneIcon from '@mui/icons-material/ScheduleTwoTone';
import { useEffect, useRef, useState } from 'react';
import { readChatSessionIdentity } from 'src/auth/sessionEvents';
import { pinPlatformSupportMessage, pinChatMessage } from 'src/queries/chat/api';
import { useQueryClient } from '@tanstack/react-query';
import { chatKeys } from 'src/queries/chat/keys';
import { getMessages, pinMessage } from 'src/helper/backend_helper';
import moment from 'moment';
import { config } from 'src/config';
import {
  Download,
  ExpandLess,
  ExpandMore,
  PushPin,
  VisibilityOff
} from '@mui/icons-material';
import { USE_LEGACY_CHAT_EVENT } from 'src/realtime/chatSocket';
import PLS from '../../../assets/images/PLS.png';

const DividerWrapper = styled(Divider)(
  ({ theme }) => `
      .MuiDivider-wrapper {
        border-radius: ${theme.general.borderRadiusSm};
        text-transform: none;
        background: ${theme.palette.background.default};
        font-size: ${theme.typography.pxToRem(13)};
        color: ${theme.colors.alpha.black[50]};
      }
`
);

const CardWrapperPrimary = styled(Card)(
  ({ theme }) => `
      background: ${theme.colors.primary.main};
      color: ${theme.palette.primary.contrastText};
      padding: ${theme.spacing(2)};
      border-radius: ${theme.general.borderRadiusXl};
      border-top-right-radius: ${theme.general.borderRadius};
      max-width: 380px;
      display: inline-flex;
`
);

const CardWrapperSecondary = styled(Card)(
  ({ theme }) => `
      background: ${theme.colors.alpha.black[10]};
      color: ${theme.colors.alpha.black[100]};
      padding: ${theme.spacing(2)};
      border-radius: ${theme.general.borderRadiusXl};
      border-top-left-radius: ${theme.general.borderRadius};
      max-width: 380px;
      display: inline-flex;
`
);

function handleOpenImage(url) {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
}

function ChatContent({ chat, messages, setMessages, scrollToBottom, socket, supportScope = 'legacy', managedMessages = false }) {
  const cache = useQueryClient();
  const sessionIdentity = useRef(readChatSessionIdentity()).current;
  const user = JSON.parse(localStorage.getItem('PLS'))?.user;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalMessages, setTotalMessages] = useState(0);
  const [count, setCount] = useState(0);
    const [pinningIds, setPinningIds] = useState([]); // 👈 NEW

      const [pinnedOpen, setPinnedOpen] = useState(false);

      const[pinLoading,setPinLoading] = useState(false);

  // Helper: pinned + non-pinned messages
  const pinnedMessages = messages.filter((m) => m.isPinned); // 👈 NEW
  const regularMessages = messages

  useEffect(() => {
    if (managedMessages || supportScope === 'platform') return;
    const getData = async () => {
      const response = await getMessages(chat.id, { page: 1, pageSize: 10 });
      const sorted = response.data.data.sort((a, b) =>
        a.createdAt > b.createdAt ? 1 : -1
      );

      setMessages(sorted);
      setPage(1);
      setPageSize(10);
      setTotalMessages(response.data.meta.totalCount);
    };
    getData();
    scrollToBottom();
  }, [chat]);

  useEffect(() => {
    if (managedMessages || supportScope === 'platform') return;
    const getData = async () => {
      if (page > 1) {
        const response = await getMessages(chat.id, { page, pageSize });
        const sorted = response.data.data.sort((a, b) =>
          a.createdAt > b.createdAt ? 1 : -1
        );

        setMessages([...sorted, ...messages]);
        setTotalMessages(response.data.meta.totalCount);
      }
    };
    getData();
  }, [page]);

  useEffect(() => {
    const scrollTimer = setTimeout(() => {
      scrollToBottom();
    }, 500);
    console.log('Socket Connected', socket.connected);

    // #414: emit the new vocabulary `chat:join`. The backend gateway has a
    // legacy `'join'` shim too, so this is forwards-compatible if rolled
    // out unevenly.
    const joinChat = () => socket.emit('chat:join', { chatId: chat.id });
    joinChat();
    // Socket.IO reconnects retain the client object but lose server room membership.
    socket.on('connect', joinChat);
    return () => {
      clearTimeout(scrollTimer);
      socket.off('connect', joinChat);
    };
  }, [socket, chat]);

  useEffect(() => {
    // #414: handler for the legacy `'message'` event. Payload shape
    // matches what the backend currently emits (top-level chatId/sender/
    // content). Sprint 6 / #6.4 deletes this subscribe block.
    const handleLegacyMessage = (data) => {
      if (chat?.id === data?.chatId) {
        setMessages((msgs) => [...msgs, data]);
        scrollToBottom();
      }
    };

    // #414: handler for the new namespaced `chat:message:new` event.
    // Payload envelope is `{ chatId, message }` — the inner `message` is
    // the same shape as the legacy emit.
    const handleChatMessageNew = (envelope) => {
      const { chatId: msgChatId, message } = envelope || {};
      if (chat?.id === msgChatId) {
        setMessages((msgs) => [...msgs, message]);
        scrollToBottom();
      }
    };

    if (USE_LEGACY_CHAT_EVENT) {
      socket.on('message', handleLegacyMessage);
    }
    socket.on('chat:message:new', handleChatMessageNew);

    return () => {
      if (USE_LEGACY_CHAT_EVENT) {
        socket.off('message', handleLegacyMessage);
      }
      socket.off('chat:message:new', handleChatMessageNew);
    };
  }, [socket, chat]);



  // 👇 NEW: toggle pin / unpin
  const handleTogglePin = async (item) => {
    const nextPinned = !item.isPinned;

    setPinningIds((prev) => [...prev, item.id]);
    try {
      if (sessionIdentity !== readChatSessionIdentity()) throw new Error('Chat session changed; reopen the conversation.');
      setPinLoading(true);
      console.log('pinning', item.id, nextPinned);
      const data= {
        isPinned: nextPinned
      }
      await (supportScope === 'platform' ? pinPlatformSupportMessage(chat.id, item.id, data) : managedMessages ? pinChatMessage(chat.id, item.id, data) : pinMessage(item.id, data));
      if (sessionIdentity !== readChatSessionIdentity()) return;
      cache.invalidateQueries(chatKeys.chatMessages(chat.id));
      // Update local state
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === item.id ? { ...msg, isPinned: nextPinned } : msg
        )
      );
      setPinLoading(false);
    } catch (error) {
       setPinLoading(false);
      console.error('Error toggling pin: ', error);
    } finally {
       setPinLoading(false);
      setPinningIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

 const renderMessageBubble = (item, isOwn) => {
    const isPinning = pinningIds.includes(item.id);

    // Internal notes (issue #423): the backend persists admin-only notes as
    // SYSTEM messages of `type: 'internal'` with `metadata.internal = true`.
    // Distinguish them visually so an admin never mistakes a note for a
    // contractor-visible message.
    const isInternalNote =
      item?.type === 'internal' || item?.metadata?.internal === true;

    const Bubble =
      item.type === 'text' ||
      item.type === 'image' ||
      item.type === 'audio' ||
      item.type === 'file'
        ? isOwn
          ? CardWrapperPrimary
          : CardWrapperSecondary
        : CardWrapperSecondary;

    const content = (() => {
      if (isInternalNote) {
        return (
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.warning.main}`,
              backgroundColor: `${theme.palette.warning.light}1f`,
              borderRadius: theme.general.borderRadiusSm,
              p: 1.5,
              maxWidth: 380
            })}
            data-testid={`internal-note-${item.id}`}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ color: 'warning.dark', mb: 0.5 }}
            >
              <VisibilityOff fontSize="inherit" />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Internal note · Visible to admins only
              </Typography>
            </Box>
            <Typography variant="body2">{item.content}</Typography>
          </Box>
        );
      }
      return innerContent();
    })();

    function innerContent() {
      if (item.type === 'text') {
        return item.content;
      }
      if (item.type === 'image') {
        return (
          <Box
            onClick={() => handleOpenImage(config.FILe_URL + item.content)}
            sx={{ cursor: 'pointer' }}
          >
            <img
              src={config.FILe_URL + item.content}
              width={250}
              height={250}
            />
          </Box>
        );
      }
      if (item.type === 'audio' || item.type === 'file') {
        return (
          <Box
            onClick={() => handleOpenImage(config.FILe_URL + item.content)}
            sx={{ cursor: 'pointer' }}
          >
            {item.content} <Download />
          </Box>
        );
      }
      return item.content;
    }

    return (
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent={isOwn ? 'flex-end' : 'flex-start'}
        py={2}
        key={item.id}
      >
        {!isOwn && (
          <Avatar
            variant="rounded"
            sx={{
              width: 50,
              height: 50
            }}
            alt={item.sender?.name || 'User'}
            src={config.FILe_URL + item.sender?.profileImage}
          />
        )}

        <Box
          display="flex"
          alignItems={isOwn ? 'flex-end' : 'flex-start'}
          flexDirection="column"
          justifyContent={isOwn ? 'flex-end' : 'flex-start'}
          mx={2}
        >
          {/* Bubble + Pin icon row */}
          <Box
            display="flex"
            alignItems="flex-start"
            flexDirection={isOwn ? 'row-reverse' : 'row'}
            gap={1}
          >
            {isInternalNote ? content : <Bubble>{content}</Bubble>}


        {isPinning? (
          <CircularProgress size={20} />
        ):(
          <Tooltip title={item.isPinned ? 'Unpin' : 'Pin'}>
            <IconButton
              size="small"
              onClick={() => handleTogglePin(item)}
              disabled={isPinning}
            >
              <PushPin
                fontSize="small"
                color={item.isPinned ? 'primary' : 'disabled'}
              />
            </IconButton>
          </Tooltip>
            )}
          </Box>

          {/* time */}
          <Typography
            variant="caption"
            sx={{
              pt: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: isOwn ? 'flex-end' : 'flex-start'
            }}
          >
            <ScheduleTwoToneIcon
              sx={{
                mr: 0.5,
                fontSize: 20
              }}
            />
            {moment(item.createdAt).format('DD-MM-YYYY hh:mm a')}
          </Typography>
        </Box>

        {isOwn && (
          <Avatar
            variant="rounded"
            sx={{
              width: 50,
              height: 50
            }}
            alt={user.name}
            src={PLS}
          />
        )}
      </Box>
    );
  };






  return (
    <Box p={3}>
      {/* <DividerWrapper>
        {format(subDays(new Date(), 3), 'MMMM dd yyyy')}
      </DividerWrapper> */}



      {/* 👇 Pinned messages at the TOP like WhatsApp */}
      {pinnedMessages.length > 0 && (
        <Box
          mb={2}
          p={2}
          sx={(theme) => ({
            borderRadius: theme.general.borderRadiusLg,
            backgroundColor: theme.colors.alpha.black[5],
            border: `1px solid ${theme.colors.alpha.black[10]}`
          })}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setPinnedOpen((prev) => !prev)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <PushPin fontSize="small" color="primary" />
              <Typography variant="subtitle2">
                Pinned messages ({pinnedMessages.length})
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              {pinningIds.length > 0 && <CircularProgress size={16} />}
              {pinnedOpen ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </Box>
          </Box>

          <Collapse in={pinnedOpen} timeout="auto" unmountOnExit>
            <Box mt={1}>
              {pinnedMessages.map((item) =>
                renderMessageBubble(
                  item,
                  item.senderId === user.id,
                  // true // in pinned section
                )
              )}
            </Box>
          </Collapse>
        </Box>
      )}


      {totalMessages > pageSize && messages.length < totalMessages && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => setPage(page + 1)}
            // onKeyDown={() => handleNextChat()}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
          >
            View Previous Messages
          </Button>
        </div>
      )}

      {/* {messages.map((item) => {
        return item.senderId !== user.id ? (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
            py={3}
            key={item.id}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 50,
                height: 50
              }}
              alt="Zain Baptista"
              src={config.FILe_URL + item.sender.profileImage}
            />
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              justifyContent="flex-start"
              ml={2}
            >
              {item.type === 'text' && (
                <CardWrapperSecondary>{item.content}</CardWrapperSecondary>
              )}
              {item.type === 'image' && (
                <CardWrapperSecondary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <img
                    src={config.FILe_URL + item.content}
                    width={250}
                    height={250}
                  />
                </CardWrapperSecondary>
              )}

              {item.type === 'audio' && (
                <CardWrapperSecondary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  {item.content} <Download />
                </CardWrapperSecondary>
              )}
              {item.type === 'file' && (
                <CardWrapperSecondary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  {item.content} <Download />
                </CardWrapperSecondary>
              )}
              <Typography
                variant="caption"
                sx={{
                  pt: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ScheduleTwoToneIcon
                  sx={{
                    mr: 0.5,
                    fontSize: 20
                  }}
                  // fontSize={10}
                />
                {moment(item.createdAt).format('DD-MM-YYYY hh:mm a')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-end"
            py={3}
          >
            <Box
              display="flex"
              alignItems="flex-end"
              flexDirection="column"
              justifyContent="flex-end"
              mr={2}
            >
              {item.type === 'text' && (
                <CardWrapperPrimary>{item.content}</CardWrapperPrimary>
              )}
              {item.type === 'image' && (
                <CardWrapperPrimary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <img
                    src={config.FILe_URL + item.content}
                    width={250}
                    height={250}
                  />
                </CardWrapperPrimary>
              )}

              {item.type === 'audio' && (
                <CardWrapperPrimary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  {item.content} <Download />
                </CardWrapperPrimary>
              )}
              {item.type === 'file' && (
                <CardWrapperPrimary
                  onClick={() =>
                    handleOpenImage(config.FILe_URL + item.content)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  {item.content} <Download />
                </CardWrapperPrimary>
              )}
              <Typography
                variant="caption"
                sx={{
                  pt: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ScheduleTwoToneIcon
                  sx={{
                    mr: 0.5,
                    fontSize: 20
                  }}
                  // fontSize="small"
                />

                {moment(item.createdAt).format('DD-MM-YYYY hh:mm a')}
              </Typography>
            </Box>
            <Avatar
              variant="rounded"
              sx={{
                width: 50,
                height: 50
              }}
              alt={user.name}
              src={PLS}
            />
          </Box>
        );
      })} */}
        {/* Regular (non-pinned) messages in normal order */}
      {regularMessages.map((item) =>
        renderMessageBubble(item, item.senderId === user.id)
      )}

      {/* <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-end"
        py={3}
      >
        <Box
          display="flex"
          alignItems="flex-end"
          flexDirection="column"
          justifyContent="flex-end"
          mr={2}
        >
          <CardWrapperPrimary>
            Yes, I'll email them right now. I'll let you know once the remaining
            invoices are done.
          </CardWrapperPrimary>
          <Typography
            variant="subtitle1"
            sx={{
              pt: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ScheduleTwoToneIcon
              sx={{
                mr: 0.5
              }}
              fontSize="small"
            />
            {formatDistance(subHours(new Date(), 125), new Date(), {
              addSuffix: true
            })}
          </Typography>
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            width: 50,
            height: 50
          }}
          alt={user.name}
          src={user.avatar}
        />
      </Box>
      <DividerWrapper>
        {format(subDays(new Date(), 5), 'MMMM dd yyyy')}
      </DividerWrapper>

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-end"
        py={3}
      >
        <Box
          display="flex"
          alignItems="flex-end"
          flexDirection="column"
          justifyContent="flex-end"
          mr={2}
        >
          <CardWrapperPrimary>Hey! Are you there?</CardWrapperPrimary>
          <CardWrapperPrimary
            sx={{
              mt: 2
            }}
          >
            Heeeelloooo????
          </CardWrapperPrimary>
          <Typography
            variant="subtitle1"
            sx={{
              pt: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ScheduleTwoToneIcon
              sx={{
                mr: 0.5
              }}
              fontSize="small"
            />
            {formatDistance(subHours(new Date(), 60), new Date(), {
              addSuffix: true
            })}
          </Typography>
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            width: 50,
            height: 50
          }}
          alt={user.name}
          src={user.avatar}
        />
      </Box>
      <DividerWrapper>Today</DividerWrapper>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
        py={3}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 50,
            height: 50
          }}
          alt="Zain Baptista"
          src="/static/images/avatars/2.jpg"
        />
        <Box
          display="flex"
          alignItems="flex-start"
          flexDirection="column"
          justifyContent="flex-start"
          ml={2}
        >
          <CardWrapperSecondary>Hey there!</CardWrapperSecondary>
          <CardWrapperSecondary
            sx={{
              mt: 1
            }}
          >
            How are you? Is it ok if I call you?
          </CardWrapperSecondary>
          <Typography
            variant="subtitle1"
            sx={{
              pt: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ScheduleTwoToneIcon
              sx={{
                mr: 0.5
              }}
              fontSize="small"
            />
            {formatDistance(subMinutes(new Date(), 6), new Date(), {
              addSuffix: true
            })}
          </Typography>
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-end"
        py={3}
      >
        <Box
          display="flex"
          alignItems="flex-end"
          flexDirection="column"
          justifyContent="flex-end"
          mr={2}
        >
          <CardWrapperPrimary>
            Hello, I just got my Amazon order shipped and I’m very happy about
            that.
          </CardWrapperPrimary>
          <CardWrapperPrimary
            sx={{
              mt: 1
            }}
          >
            Can you confirm?
          </CardWrapperPrimary>
          <Typography
            variant="subtitle1"
            sx={{
              pt: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ScheduleTwoToneIcon
              sx={{
                mr: 0.5
              }}
              fontSize="small"
            />
            {formatDistance(subMinutes(new Date(), 8), new Date(), {
              addSuffix: true
            })}
          </Typography>
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            width: 50,
            height: 50
          }}
          alt={user.name}
          src={user.avatar}
        />
      </Box> */}
    </Box>
  );
}

export default ChatContent;
